import React from 'react';
import { Box, Alert, Button, Typography, Paper, Stack, IconButton, Collapse, Card, CardContent, Divider } from '@mui/material';
import {
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Security as SecurityIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
            isAuthenticated: true
        };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            error,
            errorInfo
        });

        // Log error to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('Error Boundary caught an error:', error, errorInfo);
        }

        // Check if it's an authentication error
        if (
            error.message?.includes('UNAUTHENTICATED') ||
            error.message?.includes('Access denied') ||
            error.message?.includes('no user found')
        ) {
            this.setState({ isAuthenticated: false });
        }
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
            isAuthenticated: true
        });
    };

    handleLogin = () => {
        // Clear any stored tokens
        sessionStorage.clear();
        localStorage.clear();

        // Redirect to login or refresh the page
        window.location.reload();
    };

    handleToggleDetails = () => {
        this.setState((prevState) => ({
            showDetails: !prevState.showDetails
        }));
    };

    render() {
        if (this.state.hasError) {
            const { error, isAuthenticated, showDetails } = this.state;

            // Authentication Error
            if (!isAuthenticated) {
                return (
                    <Box
                        sx={{
                            minHeight: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            p: 3
                        }}
                    >
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                            <Card
                                sx={{
                                    maxWidth: 500,
                                    width: '100%',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <SecurityIcon
                                        sx={{
                                            fontSize: 64,
                                            color: 'warning.main',
                                            mb: 2
                                        }}
                                    />

                                    <Typography variant="h5" gutterBottom fontWeight="bold">
                                        Authentication Required
                                    </Typography>

                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                        Your session has expired or you don't have permission to access this resource. Please log in again
                                        to continue.
                                    </Typography>

                                    <Stack spacing={2}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={this.handleLogin}
                                            startIcon={<RefreshIcon />}
                                            sx={{ py: 1.5 }}
                                        >
                                            Re-authenticate
                                        </Button>

                                        <Button variant="outlined" onClick={this.handleRetry} size="small">
                                            Try Again
                                        </Button>
                                    </Stack>

                                    {process.env.NODE_ENV === 'development' && (
                                        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                            <Button
                                                size="small"
                                                onClick={this.handleToggleDetails}
                                                startIcon={showDetails ? <CloseIcon /> : <InfoIcon />}
                                            >
                                                {showDetails ? 'Hide' : 'Show'} Error Details
                                            </Button>

                                            <Collapse in={showDetails}>
                                                <Paper sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                                                    <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
                                                        {error?.message}
                                                    </Typography>
                                                </Paper>
                                            </Collapse>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>
                    </Box>
                );
            }

            // General Error
            return (
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        p: 3
                    }}
                >
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <Card
                            sx={{
                                maxWidth: 600,
                                width: '100%',
                                textAlign: 'center',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                            }}
                        >
                            <CardContent sx={{ p: 4 }}>
                                <ErrorIcon
                                    sx={{
                                        fontSize: 64,
                                        color: 'error.main',
                                        mb: 2
                                    }}
                                />

                                <Typography variant="h5" gutterBottom fontWeight="bold">
                                    Something went wrong
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    We encountered an unexpected error. Our team has been notified and is working to fix it.
                                </Typography>

                                <Stack spacing={2} alignItems="center">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={this.handleRetry}
                                        startIcon={<RefreshIcon />}
                                        sx={{ py: 1.5 }}
                                    >
                                        Try Again
                                    </Button>

                                    <Button variant="outlined" onClick={() => window.location.reload()} size="small">
                                        Refresh Page
                                    </Button>
                                </Stack>

                                {process.env.NODE_ENV === 'development' && (
                                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                        <Button
                                            size="small"
                                            onClick={this.handleToggleDetails}
                                            startIcon={showDetails ? <CloseIcon /> : <WarningIcon />}
                                        >
                                            {showDetails ? 'Hide' : 'Show'} Error Details
                                        </Button>

                                        <Collapse in={showDetails}>
                                            <Paper sx={{ mt: 2, p: 2, bgcolor: 'grey.50', textAlign: 'left' }}>
                                                <Typography variant="h6" gutterBottom>
                                                    Error Details:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    component="pre"
                                                    sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
                                                >
                                                    {error?.message}
                                                </Typography>

                                                {this.state.errorInfo && (
                                                    <>
                                                        <Divider sx={{ my: 2 }} />
                                                        <Typography variant="h6" gutterBottom>
                                                            Stack Trace:
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            component="pre"
                                                            sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
                                                        >
                                                            {this.state.errorInfo.componentStack}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Paper>
                                        </Collapse>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
